/*
 *
 * File: PassengerLoginScreen.js
 *
 * Purpose: Multi waypoints route calculation component file.
 *
 * Author:   Thomas Burgard (Software development)
 * Created:  14.04.2023
 * Modified: 14.04.2023
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { React, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";
import { getUserRegistrationData } from "../utils/utils";
import { getPassengerData } from "../store/actions";
import loginImg from "../img/login.svg";

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const regEmail =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const initialState = {
  username: "",
  password: "",
};

let userRegistrationDataObj = null;
//---End component variable declarations -------------------------

/**
 * Implementation of passenger login screen component.
 *
 * @param {*} props
 * @returns JSX-HTML-DOM
 */
const PassengerLoginScreen = (props) => {
  const userData = props.userData;
  const whiteLabel = props.userData.regData.WHITELABEL;
  const { t, i18n } = useTranslation("common");
  const selectedLanguage = useSelector((state) => state.languageReducer);

  //--------------------------------------------------------------
  //--- REDUX ----------------------------------------------------
  //--------------------------------------------------------------
  const dispatch = useDispatch();
  const passengerServerData = useSelector((state) => state.passengerReducer);

  //--------------------------------------------------------------
  //--- Navigation -----------------------------------------------
  //--------------------------------------------------------------
  const navigate = useNavigate();

  //--------------------------------------------------------------
  //--- Component states -----------------------------------------
  //--------------------------------------------------------------
  const [values, setValues] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  //--- End states -----------------------------------------------

  userRegistrationDataObj = getUserRegistrationData(userData);

  //--------------------------------------------------------------
  //--- Will be called after "first" rendering! ------------------
  //--------------------------------------------------------------
  useEffect(() => {}, []);

  //--------------------------------------------------------------
  //--- Will be called after "passengerServerData" changed -------
  //--------------------------------------------------------------
  useEffect(() => {
    if (passengerServerData.passengerData.PassengerData !== "Initial") {
      if (
        passengerServerData.passengerData.PassengerData !==
        "Wrong passenger data"
      ) {
        setTimeout(() => {
          navigate("/passengeracceptbookings");
        }, 600);
      } else if (passengerServerData.passengerData.PassengerData ===
        "Error") {
        toast.error(t("errortext.error-text-server-error"));        
      } else {
        toast.error(t("errortext.error-login-text"));
      }
    }
  }, [passengerServerData]);

  /**
   * Handle input text change event.
   *
   * @param {*} e form event
   */
  const handleChange = (e) => {
    const { username, password } = values;
    const name = e.target.name;
    const value = e.target.value;

    // Set dynamically the object keys
    // with square brackets "[name]""
    setValues({ ...values, [name]: value });

    if (e.key === "Enter") {
      setIsLoading(true);
      dispatch(
        getPassengerData(username, password, userRegistrationDataObj.userID)
      );
    }
  };

  /**
   * Submit the login form with fields input check.
   *
   * @param {*} e form event
   */
  const onSubmit = (e) => {
    const { username, password } = values;

    e.preventDefault();

    if (!username || !password) {
      toast.error(t("errortext.error-validation-text"));
      return;
    }

    setIsLoading(true);
    dispatch(
      getPassengerData(username, password, userRegistrationDataObj.userID)
    );
  };

  //--------------------------------------------------------------
  //--- Returns JSX code for "PassengerLoginScreen ---------------
  //--------------------------------------------------------------
  return (
    <>
      <Header userData={userData} style={{ left: "0", right: "0" }} />
      <div className="grid grid-nogutter surface-section">
        <div className="col-12 p-2 py-8 flex align-items-center justify-content-center">
          <div className="surface-card p-4 shadow-2 border-round w-full lg:w-6">
            <div className="text-center mb-5">
              <img src={loginImg} alt="" width={250} className="mb-3" />
              <div className="text-900 text-3xl font-medium mb-3">
                {t("screentext.screen-text-login-header1")} -{" "}
                {t("screentext.screen-text-login")}
              </div>
            </div>

            {/* Begin: form ************************************************/}
            <form onSubmit={onSubmit}>
              <div className="input-group">
                <label htmlFor="username">
                  {t("formular.placeholder-username")}
                </label>
                <InputText
                  type="text"
                  name="username"
                  onChange={handleChange}
                  className="w-full mt-2 mb-4"
                />
              </div>
              <div className="input-group">
                <label htmlFor="password">
                  {t("formular.placeholder-password")}
                </label>
                <Password
                  id="password"
                  name="password"
                  onChange={handleChange}
                  feedback={false}
                  inputStyle={{ width: "100%" }}
                  className="mt-2"
                  style={{ width: "100%" }}
                  autocomplete="new-password"
                  toggleMask
                />
              </div>
              <div className="input-group">
                <Button
                  type="submit"
                  label={
                    isLoading
                      ? t("formular.btn-wait")
                      : t("formular.btn-passenger-login")
                  }
                  icon="pi pi-sign-in"
                  className="mt-4 w-full"
                  size="lg"
                />
              </div>
            </form>
            {/* End: form */}
            <Button
              label={t("formular.btn-passenger-register")}
              link
              className="mt-3"
              onClick={() => navigate("/passengerregister")}
            />
          </div>
        </div>
      </div>
      <Footer userData={userData} />
    </>
  );
};

export default PassengerLoginScreen;
